import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { X, Send, Loader2, LifeBuoy } from 'lucide-react';

const CHUY_AVATAR = "/chuy.png";

export const ChuyChat = () => {
  const { user, api } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: t('chuy.greeting')
      }]);
    }
  }, [isOpen, messages.length, t]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api().post('/chuy/chat', {
        message: userMessage,
        language: language
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: language === 'en' 
          ? "Oops! I'm having a little siesta moment. Try again in a sec! 😅"
          : "¡Ups! Estoy teniendo un momento de siesta. ¡Intenta de nuevo! 😅"
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!user) return null;
  // Hide on auth and on screens that already have heavy bottom CTAs or persistent Chuy card
  const hiddenRoutes = ['/auth', '/chuy-help', '/chuy-chat', '/dashboard', '/profile', '/receipts', '/withdrawals', '/partner/withdrawals', '/bookings', '/provider/catalog', '/provider/requests'];
  const hiddenPrefixes = ['/dashboard/create-request', '/dashboard/bookings/', '/dashboard/requests/', '/payment/', '/product/'];
  if (hiddenRoutes.includes(location.pathname)) return null;
  if (hiddenPrefixes.some((p) => location.pathname.startsWith(p))) return null;

  return (
    <>
      {/* Chat Button with pill CTA */}
      {!isOpen && (
        <button
          data-testid="chuy-chat-btn"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-4 z-[60] flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-card border border-border shadow-2xl hover:shadow-caribbean/30 hover:-translate-y-0.5 transition-all group max-w-[260px]"
        >
          <span className="relative">
            <img
              src={CHUY_AVATAR}
              alt="Chuy"
              className="w-10 h-10 rounded-full object-cover border-2 border-caribbean"
            />
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-rosa rounded-full border-2 border-card animate-pulse" />
          </span>
          <span className="text-xs font-semibold text-foreground leading-tight text-left">
            {t('chuy.cta')}
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chuy-chat-window"
          className="fixed bottom-24 right-4 z-[60] w-[340px] max-w-[calc(100vw-2rem)] h-[480px] max-h-[calc(100vh-8rem)] bg-card rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in border border-border"
        >
          {/* Header */}
          <div className="bg-caribbean p-3 flex items-center gap-3">
            <img
              src={CHUY_AVATAR}
              alt="Chuy"
              className="w-10 h-10 rounded-full object-cover border-2 border-white/50"
            />
            <div className="flex-1">
              <p className="font-heading font-bold text-white">Chuy</p>
              <p className="text-[11px] text-white/80">
                {language === 'en' ? 'Your 24/7 Concierge' : 'Tu Concierge 24/7'}
              </p>
            </div>
            <Link
              to="/chuy-help"
              onClick={() => setIsOpen(false)}
              data-testid="chuy-help-shortcut"
              className="text-white/80 hover:text-white transition-colors p-1"
              title={t('chuy.help')}
            >
              <LifeBuoy size={18} />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors p-1"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea ref={scrollRef} className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-caribbean text-white rounded-br-sm'
                        : 'bg-muted rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-muted p-3 rounded-2xl rounded-bl-sm">
                    <Loader2 className="animate-spin text-caribbean" size={20} />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex items-center gap-2">
              <Input
                data-testid="chuy-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chuy.placeholder')}
                className="flex-1 h-10 rounded-full"
                disabled={loading}
              />
              <Button
                data-testid="chuy-send-btn"
                onClick={handleSend}
                disabled={loading || !input.trim()}
                size="icon"
                className="w-10 h-10 rounded-full bg-caribbean hover:bg-caribbean/90"
              >
                <Send size={18} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
